import classNames from 'classnames';
import { Disclosure } from '@headlessui/react';
import { motion } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';
import Title from '../../Atoms/Title';
import Icon from '../../Atoms/Icon';

interface ItemProps {
  title: string;
  isOpened: boolean;
  content: string | string[] | JSX.Element | JSX.Element[];
  selection?: string[];
}

interface Props {
  elements: ItemProps[];
  className?: string;
}

const animation = {
  open: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: 0.1
    }
  },
  closed: {
    opacity: 0.3,
    height: 0,
    transition: {
      duration: 0.15
    }
  }
};

function AccordionFilterItem({
  title,
  isOpened,
  content,
  selection
}: ItemProps) {
  const isMobileView = useMediaQuery({
    query: '(max-width: 1023px)'
  });

  return (
    <Disclosure
      as="div"
      defaultOpen={!isMobileView && isOpened}
      className="w-full rounded-xl border border-sl-blue-dark border-opacity-10 bg-white py-3 px-5 text-left shadow-light-sm md:py-4 md:px-5 lg:border-transparent lg:p-0 lg:shadow-none"
    >
      {({ open }) => (
        <>
          <Disclosure.Button
            as="div"
            className="flex w-full cursor-pointer select-none justify-between"
          >
            <div className="flex flex-col gap-y-1">
              <Title
                as="h5"
                size={6}
                color="blue"
                leading="tight"
              >
                <span className="text-sm text-sl-blue-dark md:text-base lg:text-lg lg:text-inherit">
                  {title}
                </span>
              </Title>
              <span
                className={classNames([
                  'font-circular text-[12px] font-normal leading-tight lg:hidden',
                  selection
                    ? 'text-sl-indigo'
                    : 'text-sl-gray'
                ])}
              >
                {!selection && <span>Geen selectie</span>}
                {selection && (
                  <span>
                    {selection[0]}
                    {selection.length > 1 &&
                      ` + ${selection.length - 1}`}
                  </span>
                )}
              </span>
            </div>
            <span className="inline-flex shrink-0 basis-5 justify-end self-center text-xl !leading-none lg:mr-1 lg:self-start lg:pt-1.5">
              <span className="hidden lg:inline-flex">
                {open ? (
                  <Icon icon="minus" />
                ) : (
                  <Icon icon="plus" />
                )}
              </span>
              <span
                className={`${
                  open ? 'rotate-90' : 'rotate-0'
                } inline-flex text-base duration-150 lg:hidden`}
              >
                <Icon icon="chevron-right" />
              </span>
            </span>
          </Disclosure.Button>
          <motion.div
            variants={animation}
            animate={open ? 'open' : 'closed'}
            className={classNames([
              !open ? 'overflow-hidden' : '',
              'select-none'
            ])}
          >
            <div className="pt-7 pb-3 lg:py-7">
              {content}
            </div>
          </motion.div>
        </>
      )}
    </Disclosure>
  );
}

function AccordionFilters({ elements, className }: Props) {
  return (
    <div
      className={classNames(
        'flex flex-col gap-y-4 lg:gap-y-8',
        className
      )}
    >
      {elements.map((el) => (
        <AccordionFilterItem
          key={elements.indexOf(el)}
          {...el}
        />
      ))}
    </div>
  );
}

export default AccordionFilters;
